import { ApolloCache, DocumentNode, gql } from '@apollo/client';
import * as React from 'react';

import { useCurrent } from '@/hooks/useCurrent';

import { UpdateCacheOrganizationFragment } from './graphql-types/UpdateCacheOrganizationFragment';
import { UseUpdateCacheOnPostCreation_PostV2Fragment } from './graphql-types/UseUpdateCacheOnPostCreation_PostV2Fragment';

const FRAGMENT = gql`
  fragment UseUpdateCacheOnPostCreation_PostV2Fragment on PostV2 {
    _id
    organization {
      _id
    }
    announcement {
      _id
    }
    datePublished
  }
`;

type UpdateCacheOnPostCreationMethod = (options: {
  cache: ApolloCache<any>;
  post: UseUpdateCacheOnPostCreation_PostV2Fragment;
}) => void;

type WithFragment = { postFragment: DocumentNode };
type UseUpdateCacheOnPostCreation = () => {
  updateCacheOnPostCreation: UpdateCacheOnPostCreationMethod;
};

// if the use case of updating the cache on post creation ever becomes significantly more complex,
// the best way to do this may be to pass down specific cache updating methods from a parent component
// (e.g the home feed) to the post mutation in order to customize cache behavior per instance of
// post creation, as opposed to having one cache update method that fits all of these caseses.
export const useUpdateCacheOnPostCreation: UseUpdateCacheOnPostCreation &
  WithFragment = (): {
  updateCacheOnPostCreation: UpdateCacheOnPostCreationMethod;
} => {
  const current = useCurrent();

  const updateCacheOnPostCreation: UpdateCacheOnPostCreationMethod =
    React.useCallback(
      ({ cache, post }) => {
        cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'activityByAuthorIdV2',
          args: { input: { authorId: current.user?._id } },
        });

        if (!!post.organization?._id) {
          const organizationFragment = gql`
            fragment UpdateCacheOrganizationFragment on Organization {
              _id
              name
              recentActivityV2(input: {}) {
                results {
                  _id
                }
                hasMore
              }
            }
          `;

          const cachedOrganization =
            cache.readFragment<UpdateCacheOrganizationFragment>({
              id: cache.identify({ ...post.organization }),
              fragment: organizationFragment,
            });

          if (
            cachedOrganization?.recentActivityV2.results.every(
              (cachedPost) => cachedPost._id !== post._id
            )
          ) {
            cache.writeFragment<UpdateCacheOrganizationFragment>({
              id: cache.identify({ ...post.organization }),
              fragment: organizationFragment,
              data: {
                ...cachedOrganization,
                recentActivityV2: {
                  ...cachedOrganization.recentActivityV2,
                  results: [
                    post,
                    ...cachedOrganization.recentActivityV2.results,
                  ],
                },
              },
            });
          }
        }

        cache.modify({
          fields: {
            // only works because postFeed refreshes w/ network-only... we need to be more selective
            // if that is to change (we would need logic on correctly adding this to each postFeed, e.g group and org)P
            postFeedV2(existingItems, { toReference }) {
              //@ts-ignore
              const cacheItemRef = toReference(post);

              const newItems = {
                ...existingItems,
                posts: [cacheItemRef, ...existingItems.posts],
              };
              return newItems;
            },
            myDraftPosts(existingItems, { toReference }) {
              if (
                !(
                  post.__typename === 'SimplePost' ||
                  post.__typename === 'BlogV2' ||
                  post.__typename === 'PollV2' ||
                  post.__typename === 'QuestionV2'
                ) ||
                post.datePublished
              ) {
                return existingItems;
              }

              //@ts-ignore
              const cacheItemRef = toReference(post);

              const newItems = {
                ...existingItems,
                posts: [cacheItemRef, ...existingItems.posts],
              };
              return newItems;
            },
            announcementFeedV2(existingItems, { toReference }) {
              if (!post.announcement) {
                return existingItems;
              }
              //@ts-ignore
              const cacheItemRef = toReference(post);

              const newItems = {
                ...existingItems,
                posts: [cacheItemRef, ...existingItems.posts],
              };
              return newItems;
            },
          },
        });
      },
      [current.user?._id]
    );

  return {
    updateCacheOnPostCreation,
  };
};

useUpdateCacheOnPostCreation.postFragment = FRAGMENT;
